/* eslint-disable max-lines-per-function */

import { Button, Center, Checkbox, Flex, Grid, Loader, Stack, Switch, Title } from '@mantine/core';
import { capitalize } from 'lodash';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { EnvironmentCountries, ExternalSystem, LowValuesDeclarantDataSource } from '@e-origin/shared';

import { useAdminDeclarant } from '@admin/hooks';
import { BackButton, LeavePageNotification, NumberInput, PasswordInput, Select, TextInput } from '@components';
import { ContentLayout } from '@features/layout';
import { useSettings } from '@hooks/useSettings';
import { useEditDeclarantFormInitializer } from './useEditDeclarantFormInitializer';

const externalSystemNames = [
  { label: 'Xcomm', value: ExternalSystem.xcomm },
  { label: 'Begate', value: ExternalSystem.begate },
  { label: 'Disabled xcomm', value: ExternalSystem['disabled-xcomm'] },
];

const LOW_VALUES_DECLARANT_DATA_SOURCE_OPTIONS = Object.values(LowValuesDeclarantDataSource).map((value) => ({
  value,
  label: value === LowValuesDeclarantDataSource.DECLARANT ? 'Manage account' : capitalize(value),
}));

export const EditDeclarant = () => {
  const { id } = useParams();
  const { data: declarant, isLoading } = useAdminDeclarant(id);
  const { data: settings } = useSettings();

  const { form, buildInitialValues, onSubmit } = useEditDeclarantFormInitializer({ declarant });

  useEffect(() => {
    form.setValues(buildInitialValues(declarant));
    form.resetDirty();
    form.resetTouched();
  }, [declarant]);

  const changesSelectedExternalSystemName = (value) => {
    form.setFieldValue('externalSystemCustoms.systemName', value ? externalSystemNames.find((system) => system.value === value).value : null);
  };

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  const renderCounterField = (path, label, isSequence = false) => (
    <Stack mt={16}>
      <Flex align="center" gap={32}>
        <Title order={5} fw={600} display="flex">
          {label}
        </Title>
        <Switch {...form.getInputProps(`${path}Reset`)} size="sm" label={`Reset ${isSequence ? 'sequence' : 'counter'}`} />
      </Flex>
      {declarant[path].map((_, counterIdx) => (
        <Grid key={`${path}-${counterIdx}`}>
          <Grid.Col span={5}>
            <NumberInput value={declarant[path][counterIdx].year} label="Year" disabled />
          </Grid.Col>
          <Grid.Col span={5}>
            <NumberInput value={declarant[path][counterIdx][isSequence ? 'sequence' : 'counter']} label={isSequence ? 'Sequence' : 'Counter'} disabled />
          </Grid.Col>
        </Grid>
      ))}
    </Stack>
  );

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />
      <ContentLayout>
        <ContentLayout.Header>
          <BackButton backPath={-1} />
        </ContentLayout.Header>
        <ContentLayout.Content>
          <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack mt={20}>
              <Grid>
                <Grid.Col span={4}>
                  <TextInput {...form.getInputProps('name')} label="Name" />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextInput {...form.getInputProps('accountName')} label="Account name" />
                </Grid.Col>
              </Grid>

              <Grid align="center">
                <Grid.Col span={3}>
                  <TextInput {...form.getInputProps('language')} label="Language" />
                </Grid.Col>

                <Grid.Col span={9} display="flex">
                  <Checkbox
                    {...form.getInputProps('deleted', { type: 'checkbox' })}
                    label="Active"
                    checked={!form.values.deleted}
                    onChange={(event) => form.setFieldValue('deleted', !event.currentTarget.checked)}
                    mr={32}
                  />
                  <Checkbox
                    {...form.getInputProps('default', { type: 'checkbox' })}
                    label="Default"
                    checked={form.values.default}
                    onChange={(event) => form.setFieldValue('default', event.currentTarget.checked)}
                  />
                </Grid.Col>
              </Grid>

              <Grid>
                {declarant.invoiceCounter?.length > 0 && <Grid.Col span={4}>{renderCounterField('invoiceCounter', 'Invoice counter')}</Grid.Col>}
                {declarant.declarationCounter?.length > 0 && <Grid.Col span={4}>{renderCounterField('declarationCounter', 'Declaration counter')}</Grid.Col>}
                {declarant.nlSequence?.length > 0 && <Grid.Col span={4}>{renderCounterField('nlSequence', 'NL sequence', true)}</Grid.Col>}
              </Grid>

              <Stack mt={16}>
                <Title order={5} fw={600} display="flex">
                  External system customs
                </Title>

                <Grid>
                  <Grid.Col span={3}>
                    <Select
                      label="Select system name"
                      data={externalSystemNames}
                      {...form.getInputProps('externalSystemCustoms.systemName')}
                      onChange={changesSelectedExternalSystemName}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.systemProtocol')} label="System protocol" />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.hostname')} label="Hostname" />
                  </Grid.Col>
                  <Grid.Col span={2}>
                    <NumberInput
                      {...form.getInputProps('externalSystemCustoms.port')}
                      label="Port"
                      onChange={(event) => {
                        form.setFieldValue('externalSystemCustoms.port', !Number.isNaN(Number(event)) ? Number(event) : null);
                      }}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.username')} label="Username" />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <PasswordInput {...form.getInputProps('externalSystemCustoms.password')} label="Password" />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.incomingPath')} label="Incoming path" />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.outgoingPath')} label="Outgoing path" />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.incomingPathBegateFreed')} label="Incoming path begate freed" />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.incomingPathBegateBlocked')} label="Incoming path begate blocked" />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.incomingPathBegateError')} label="Incoming path begate error" />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={3}>
                    <TextInput {...form.getInputProps('externalSystemCustoms.outgoingPathBeGate')} label="Outgoing path BE gate" />
                  </Grid.Col>
                </Grid>
              </Stack>

              {settings?.country === EnvironmentCountries.NL && (
                <Stack mt={16}>
                  <Title order={5} fw={600} display="flex">
                    Customs authentication
                  </Title>

                  <Grid>
                    <Grid.Col span={3}>
                      <TextInput
                        {...form.getInputProps('customsAuthentication.transitProcessIdSequenceNumber')}
                        label="NCTS Process id sequence number"
                        maxLength={2}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              )}
            </Stack>

            <Stack mt="lg">
              <Title order={5} fw="bold">
                Functional features
              </Title>
              <Grid>
                <Grid.Col span={4}>
                  <Select
                    label="H7 Low Value Import Representative and Declarant data source"
                    placeholder="Select a low values import data source"
                    data={LOW_VALUES_DECLARANT_DATA_SOURCE_OPTIONS}
                    {...form.getInputProps('functionalFeatures.lowValuesDeclarantDataSource')}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Checkbox
                    pt={15}
                    {...form.getInputProps('functionalFeatures.autoB2CSplitLVHV', { type: 'checkbox' })}
                    label="Auto B2C Split LV HV"
                    checked={form.values.functionalFeatures?.autoB2CSplitLVHV || false}
                    onChange={(event) => form.setFieldValue('functionalFeatures.autoB2CSplitLVHV', event.currentTarget.checked)}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Checkbox
                    pt={15}
                    {...form.getInputProps('functionalFeatures.showNewLvReportColumns', { type: 'checkbox' })}
                    label="Use new customs report LV columns"
                    checked={form.values.functionalFeatures?.showNewLvReportColumns || false}
                    onChange={(event) => form.setFieldValue('functionalFeatures.showNewLvReportColumns', event.currentTarget.checked)}
                  />
                </Grid.Col>
              </Grid>
            </Stack>

            <Button type="submit" disabled={!form.isValid()} variant="filled" size="lg" mt={32} mb={20}>
              Save
            </Button>
          </form>
        </ContentLayout.Content>
      </ContentLayout>
    </>
  );
};
